/**
 * L1J Web Client - Shop Dialog
 *
 * NPC 상점 목록 창 (기본 숨김). 구매 모드와 판매 모드를 한 창에서 처리한다.
 *
 * **서버의 "sell"과 플레이어의 "판매"는 반대말이다.** 서버 클래스명은 NPC 관점이고
 * 화면 문구는 플레이어 관점이라, 이 파일에서는 다음 대응을 끝까지 지킨다:
 *
 *   | 서버 패킷 | gameState 이벤트 | 이 창의 mode | 화면 문구 |
 *   |-----------|------------------|--------------|-----------|
 *   | S_ShopSellList (254) — NPC가 판다 | `shop:sellList` | `'buy'`  | 구매 |
 *   | S_ShopBuyList  (170) — NPC가 산다 | `shop:buyList`  | `'sell'` | 판매 |
 *
 * 거래 확정은 `C_Result`(shop_result) 하나이고 **`items[].id`가 모드마다 다른 것을
 * 가리킨다** — 구매는 상점 목록의 순번(shopItemIndex), 판매는 인벤토리 아이템의
 * objId다 (`types/network.ts`의 ShopResultItem 표 참조). 여기서 섞으면 서버는
 * 엉뚱한 아이템을 주거나 조용히 아무 일도 하지 않는다.
 *
 * 가격은 **개당 값**이다. 서버 `L1Shop.buyItems()`가
 * `totalPrice += order.getItem().getAssessedPrice() * count`로 곱하므로,
 * 화면의 총액도 `price * count`로 계산한다.
 *
 * 구조·스타일은 BookmarkDialog / Inventory와 같은 규약을 따른다:
 *   - Phaser Container + setScrollFactor(0) + UI 카메라 전용
 *   - 자식에게도 setScrollFactor(0)을 직접 걸어야 클릭 히트테스트가 맞는다
 *   - 목록은 Inventory와 같은 가상 스크롤 (행 오브젝트는 고정, 표시 구간만 옮긴다)
 *
 * **이 창은 씬 최상위에 붙지 않는다.** GameScene은 UI 전용 카메라를 쓰고 메인
 * 카메라가 무시할 오브젝트를 씬 시작 시 `registerUiObjects()`에서 한 번만 모으므로,
 * 나중에 씬 최상위로 만든 오브젝트는 두 카메라에 이중으로 그려진다. 그래서
 * HtmlDialog(이미 등록돼 있다)의 자식으로 붙어 카메라 배정을 물려받는다
 * (DragGhost가 같은 이유로 인벤토리의 자식이다).
 *
 * 목록은 스스로 구독한다 — GameScene을 거치지 않는다 (CharacterStatsPanel과 같은 방식).
 */

import Phaser from 'phaser';
import { gameState, ShopBuyListState } from '../../core/GameState';
import { ShopSellListPayload } from '../../types/network';
import { ConnectionManager } from '../../network/ConnectionManager';
import {
  itemIconKind,
  itemIconTextureKey,
  loadItemIcon,
  preloadItemIcons,
} from '../../data/itemIcons';

/** 플레이어 관점의 거래 방향. 서버 패킷 이름과 반대인 점은 파일 상단 표 참조. */
export type ShopMode = 'buy' | 'sell';

/** 목록 한 줄의 높이(px). */
const ROW_HEIGHT = 34;

/** 한 화면에 보여줄 줄 수. 넘는 만큼은 휠/스크롤바로 넘긴다. */
const VISIBLE_ROWS = 8;

/** 줄 배경 (평상시 · 마우스 오버 · 선택). */
const ROW_BG_COLOR = 0x2a2a3a;
const ROW_BG_HOVER_COLOR = 0x3a3a5a;
const ROW_BORDER_COLOR = 0x3a3a5a;
const ROW_BORDER_HOVER_COLOR = 0xffd700;
const ROW_BORDER_SELECTED_COLOR = 0x00ff88;

/**
 * 한 번에 살 수 있는 최대 수량.
 *
 * 서버는 아데나가 모자라면 `S_ServerMessage(189)`로 거절하므로 상한의 진실은
 * 서버에 있다. 여기서는 실수로 네 자리를 입력하는 것만 막는 안전장치다.
 */
const MAX_BUY_COUNT = 999;

/** 상점 목록 한 줄이 들고 있는 값. 모드에 따라 서로 다른 payload에서 만든다. */
interface ShopRowData {
  /** 거래 확정(shop_result)의 `items[].id`. 구매=shopItemIndex, 판매=아이템 objId. */
  id: number;
  /** 표시명. 판매 목록은 인벤토리에서 못 찾으면 비어 있다. */
  name: string;
  /** 개당 가격. */
  price: number;
  gfxId?: number;
  /** 소지 개수 (판매 모드에서만 온다). 수량 상한으로도 쓴다. */
  count?: number;
}

/** 화폐 코드 → 이름. 서버는 목록 끝에 화폐 종류를 붙여 보낸다. */
function currencyName(currency: number): string {
  switch (currency) {
    case 0x00:
      return '카이모';
    case 0x01:
      return '진주';
    case 0x07:
      return '아데나';
    default:
      return '화폐';
  }
}

/** 자릿수 구분 — 여섯 자리가 넘는 아데나는 구분 없이는 읽기 어렵다. */
function formatPrice(value: number): string {
  return Math.max(0, Math.floor(value)).toLocaleString('en-US');
}

export class ShopDialog extends Phaser.GameObjects.Container {
  private panelWidth = 560;
  private panelHeight = 440;

  /** 목록 영역 좌측 X / 상단 Y / 폭 / 높이 (모두 컨테이너 로컬 좌표). */
  private listLeft = -this.panelWidth / 2 + 12;
  private listTop = -this.panelHeight / 2 + 40;
  private listWidth = this.panelWidth - 24 - 14; // 오른쪽 14px는 스크롤바 자리
  private listHeight = VISIBLE_ROWS * ROW_HEIGHT;

  private mode: ShopMode = 'buy';
  /** 거래 상대 NPC. shop_result의 npcObjId로 그대로 나간다. */
  private npcObjId = 0;
  private currency = 0x07;
  private entries: ShopRowData[] = [];

  private rows: ShopRow[] = [];

  private titleText!: Phaser.GameObjects.Text;
  private countText!: Phaser.GameObjects.Text;
  private emptyText!: Phaser.GameObjects.Text;
  private infoText!: Phaser.GameObjects.Text;
  private qtyText!: Phaser.GameObjects.Text;
  private totalText!: Phaser.GameObjects.Text;
  private confirmText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;

  private scrollTrack!: Phaser.GameObjects.Rectangle;
  private scrollThumb!: Phaser.GameObjects.Rectangle;
  /** 트랙을 누른 채 끄는 중인지. 트랙 밖으로 나가도 이어져야 해서 씬 단위로 추적한다. */
  private isScrubbing = false;

  /**
   * 스크롤 위치(줄 단위). 줄 오브젝트는 VISIBLE_ROWS개로 고정하고 표시 구간만
   * 옮기는 가상 스크롤이다 (Inventory / BookmarkDialog와 같은 방식).
   */
  private scrollRow = 0;

  /**
   * 선택은 줄이 아니라 항목에 묶는다 — 스크롤하면 줄↔항목 대응이 바뀌므로
   * 줄에 표시를 남기면 엉뚱한 항목이 선택돼 보인다.
   */
  private selectedId: number | null = null;
  private quantity = 1;

  /** 창이 열리거나 닫힐 때마다 불린다. 부모 컨테이너의 표시 상태를 맞추는 데 쓴다. */
  public onVisibilityChange: (() => void) | null = null;

  /** 사용자가 창을 닫았을 때만 불린다 (`hide(false)`로 닫으면 불리지 않는다). */
  public onDismiss: (() => void) | null = null;

  /** 씬 input에 직접 건 리스너. destroy 때 해제해야 유령 리스너가 남지 않는다. */
  private sceneInput: Phaser.Input.InputPlugin;
  private onWheel!: (pointer: Phaser.Input.Pointer, objs: unknown, dx: number, dy: number) => void;
  private onPointerMove!: (pointer: Phaser.Input.Pointer) => void;
  private onPointerUp!: () => void;

  /** gameState 구독 해제 함수들. */
  private unsubscribers: Array<() => void> = [];

  /**
   * @param x,y 부모 컨테이너 기준 로컬 좌표.
   *
   * 씬 디스플레이 리스트에 직접 붙이지 않는다 (파일 상단 카메라 주석 참조).
   * 만든 쪽이 `parent.add(shopDialog)`로 자식에 넣어야 화면에 나온다.
   */
  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    this.sceneInput = scene.input;

    this.setScrollFactor(0);

    this.createUI();
    this.registerScrollInput();
    this.subscribeShopEvents();
    this.setVisible(false);

    // 아이콘 23종은 통틀어도 몇 KB라 한 번에 받아둔다. 줄이 그려질 때 텍스처가
    // 이미 있으면 동기 경로로 빠져 깜빡임이 없다 (Inventory와 같은 이유).
    void preloadItemIcons(scene);
  }

  public getPanelWidth(): number {
    return this.panelWidth;
  }

  public getPanelHeight(): number {
    return this.panelHeight;
  }

  /** 창이 열려 있는지. */
  public isOpen(): boolean {
    return this.visible;
  }

  // ==================== 화면 구성 ====================

  private createUI(): void {
    const w = this.panelWidth;
    const h = this.panelHeight;

    const bg = this.scene.add.rectangle(0, 0, w, h, 0x1a1a2e, 0.96);
    bg.setStrokeStyle(2, 0x4a4a6a);
    this.add(bg);

    const titleBar = this.scene.add.rectangle(0, -h / 2 + 15, w, 30, 0x2a2a4e);
    titleBar.setStrokeStyle(1, 0x3a3a5a);
    this.add(titleBar);

    this.titleText = this.scene.add.text(-w / 2 + 12, -h / 2 + 15, '상점', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      fontStyle: 'bold',
    });
    this.titleText.setOrigin(0, 0.5);
    this.add(this.titleText);

    // "보이는 범위 / 총 개수". 목록이 꽉 찼을 때 "이게 전부인지"를 알려 준다.
    this.countText = this.scene.add.text(w / 2 - 44, -h / 2 + 15, '', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#9a9ac0',
    });
    this.countText.setOrigin(1, 0.5);
    this.add(this.countText);

    const closeBtn = this.scene.add.text(w / 2 - 20, -h / 2 + 15, 'X', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ff6666',
    });
    closeBtn.setOrigin(0.5);
    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다.
    // 걸지 않으면 카메라가 스크롤된 상태에서 히트테스트가 월드 좌표로 계산돼 빗나간다.
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.setScrollFactor(0);
    closeBtn.on('pointerover', () => closeBtn.setStyle({ color: '#ffaaaa' }));
    closeBtn.on('pointerout', () => closeBtn.setStyle({ color: '#ff6666' }));
    // 닫은 뒤 씬이 이 클릭을 월드 클릭으로 오해하지 않도록 알린다
    // (Phaser는 오브젝트 핸들러를 먼저 돌리고 씬 pointerdown을 나중에 쏜다).
    closeBtn.on('pointerdown', () => {
      this.hide();
      this.emit('panelclose');
    });
    this.add(closeBtn);

    this.createRows();
    this.createScrollBar();

    this.emptyText = this.scene.add.text(0, this.listTop + this.listHeight / 2, '', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#888888',
    });
    this.emptyText.setOrigin(0.5);
    this.add(this.emptyText);

    this.createFooter();
  }

  /** 줄 오브젝트를 미리 만들어 두고 내용만 갈아 끼운다 (Inventory 슬롯과 같은 방식). */
  private createRows(): void {
    for (let i = 0; i < VISIBLE_ROWS; i++) {
      const row = new ShopRow(
        this.scene,
        this.listLeft + this.listWidth / 2,
        this.listTop + ROW_HEIGHT / 2 + i * ROW_HEIGHT,
        this.listWidth,
        ROW_HEIGHT - 2,
        (entry) => this.select(entry)
      );
      this.rows.push(row);
      this.add(row);
    }
  }

  /**
   * 스크롤바. 목록이 한 화면에 다 들어가면 숨는다.
   *
   * 썸은 순수 시각 요소이고 입력은 트랙이 받는다 — 썸은 내용량에 따라 높이가 바뀌는데
   * Phaser에서 setSize()는 이미 등록된 hitArea를 갱신하지 않아 클릭 판정이 어긋난다.
   */
  private createScrollBar(): void {
    const trackX = this.panelWidth / 2 - 14;
    const trackY = this.listTop + this.listHeight / 2;
    const trackWidth = 6;

    this.scrollTrack = this.scene.add.rectangle(trackX, trackY, trackWidth, this.listHeight, 0x0a0a15);
    this.scrollTrack.setStrokeStyle(1, 0x2a2a4a);
    this.scrollTrack.setScrollFactor(0);
    // 6px 트랙은 그대로 두면 집기 어려우므로 판정 영역만 좌우로 넓힌다.
    // hitArea 좌표는 오브젝트 원점이 아니라 좌상단 기준(0,0)~(width,height)이다.
    this.scrollTrack.setInteractive(
      new Phaser.Geom.Rectangle(-(24 - trackWidth) / 2, 0, 24, this.listHeight),
      Phaser.Geom.Rectangle.Contains
    );
    this.scrollTrack.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isScrubbing = true;
      this.scrubToPointer(pointer);
    });
    this.add(this.scrollTrack);

    this.scrollThumb = this.scene.add.rectangle(trackX, trackY, trackWidth, 40, 0x4a4a6a);
    this.scrollThumb.setScrollFactor(0);
    this.add(this.scrollThumb);

    this.updateScrollBar();
  }

  /** 목록 아래 — 선택 정보 · 수량 조절 · 총액 · 확정/닫기 버튼. */
  private createFooter(): void {
    const w = this.panelWidth;
    const left = -w / 2 + 12;
    const right = w / 2 - 12;
    const infoY = this.listTop + this.listHeight + 20;
    const qtyY = infoY + 34;
    const buttonY = infoY + 74;

    const divider = this.scene.add.rectangle(0, infoY - 16, w - 24, 1, 0x3a3a5a);
    this.add(divider);

    // 오른쪽 끝에 statusText가 같은 줄을 쓰므로 폭을 나눠 갖는다 (한 줄로 자른다).
    this.infoText = this.scene.add.text(left, infoY, '항목을 선택하세요', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#888888',
      wordWrap: { width: w - 240 },
      maxLines: 1,
    });
    this.infoText.setOrigin(0, 0.5);
    this.add(this.infoText);

    const qtyLabel = this.scene.add.text(left, qtyY, '수량', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#9a9ac0',
    });
    qtyLabel.setOrigin(0, 0.5);
    this.add(qtyLabel);

    // -10 / -1 / [값] / +1 / +10. 키보드 입력창을 두지 않는 이유는 채팅 입력과
    // 키 포커스를 다투기 때문이다 (ChatBox가 씬 키보드를 쓴다).
    this.createStepButton(left + 44, qtyY, '-10', -10);
    this.createStepButton(left + 84, qtyY, '-1', -1);

    this.qtyText = this.scene.add.text(left + 124, qtyY, '1', {
      fontSize: '13px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.qtyText.setOrigin(0.5);
    this.add(this.qtyText);

    this.createStepButton(left + 164, qtyY, '+1', 1);
    this.createStepButton(left + 204, qtyY, '+10', 10);

    this.totalText = this.scene.add.text(right, qtyY, '', {
      fontSize: '13px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
    });
    this.totalText.setOrigin(1, 0.5);
    this.add(this.totalText);

    // 거절 사유·연결 상태 안내. 서버가 조용히 무시하는 경우가 많아 한 줄 남긴다.
    this.statusText = this.scene.add.text(right, infoY, '', {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffaa66',
      align: 'right',
      wordWrap: { width: 210 },
      maxLines: 1,
    });
    this.statusText.setOrigin(1, 0.5);
    this.add(this.statusText);

    this.confirmText = this.scene.add.text(-70, buttonY, '[ 구매 ]', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.confirmText.setOrigin(0.5);
    this.confirmText.setInteractive({ useHandCursor: true });
    this.confirmText.setScrollFactor(0);
    this.confirmText.on('pointerover', () => this.confirmText.setStyle({ color: '#ffd700' }));
    this.confirmText.on('pointerout', () => this.confirmText.setStyle({ color: '#ffffff' }));
    this.confirmText.on('pointerdown', () => this.confirmTrade());
    this.add(this.confirmText);

    const closeLabel = this.scene.add.text(70, buttonY, '[ 닫기 ]', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
    });
    closeLabel.setOrigin(0.5);
    closeLabel.setInteractive({ useHandCursor: true });
    closeLabel.setScrollFactor(0);
    closeLabel.on('pointerover', () => closeLabel.setStyle({ color: '#ffd700' }));
    closeLabel.on('pointerout', () => closeLabel.setStyle({ color: '#ffffff' }));
    closeLabel.on('pointerdown', () => {
      this.hide();
      this.emit('panelclose');
    });
    this.add(closeLabel);
  }

  /** 수량 증감 버튼 하나. */
  private createStepButton(x: number, y: number, label: string, delta: number): void {
    const btn = this.scene.add.text(x, y, label, {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#cccccc',
      backgroundColor: '#2a2a4e',
      padding: { x: 6, y: 3 },
    });
    btn.setOrigin(0.5);
    btn.setInteractive({ useHandCursor: true });
    btn.setScrollFactor(0);
    btn.on('pointerover', () => btn.setStyle({ color: '#ffd700' }));
    btn.on('pointerout', () => btn.setStyle({ color: '#cccccc' }));
    btn.on('pointerdown', () => this.changeQuantity(delta));
    this.add(btn);
  }

  // ==================== 입력 (휠 / 스크롤바) ====================

  /**
   * 휠 스크롤과 스크롤바 드래그를 씬 input에 등록한다.
   *
   * 줄 배경이 패널 배경을 덮고 있어 오브젝트 단위 wheel 이벤트로는 줄 위에서 굴린 휠을
   * 놓친다. 그래서 씬 전역에서 받고 포인터가 패널 안인지 직접 판정한다
   * (Inventory / BookmarkDialog와 동일).
   */
  private registerScrollInput(): void {
    this.onWheel = (pointer, _objs, _dx, dy) => {
      if (!this.visible || !this.isPointerOverPanel(pointer)) return;
      this.setScrollRow(this.scrollRow + (dy > 0 ? 1 : -1));
    };

    this.onPointerMove = (pointer) => {
      if (this.isScrubbing) this.scrubToPointer(pointer);
    };
    this.onPointerUp = () => {
      this.isScrubbing = false;
    };

    this.sceneInput.on('wheel', this.onWheel);
    this.sceneInput.on('pointermove', this.onPointerMove);
    this.sceneInput.on('pointerup', this.onPointerUp);

    // 씬이 재시작돼도 유령 리스너가 남지 않도록 파괴 시 해제한다.
    this.once(Phaser.GameObjects.Events.DESTROY, () => {
      this.sceneInput.off('wheel', this.onWheel);
      this.sceneInput.off('pointermove', this.onPointerMove);
      this.sceneInput.off('pointerup', this.onPointerUp);
    });
  }

  /**
   * 화면상의 패널 중심. 이 창은 다른 컨테이너의 자식이라 `this.x/y`는 로컬
   * 좌표다 — 포인터(화면 좌표)와 견주려면 부모 변환까지 넣어야 한다.
   * 부모들이 모두 scrollFactor 0이므로 월드 변환이 곧 화면 좌표다.
   */
  private getScreenCenter(): { x: number; y: number } {
    const matrix = this.getWorldTransformMatrix();
    return { x: matrix.tx, y: matrix.ty };
  }

  /** 포인터가 패널 사각형 안에 있는지. */
  private isPointerOverPanel(pointer: Phaser.Input.Pointer): boolean {
    const center = this.getScreenCenter();
    return (
      Math.abs(pointer.x - center.x) <= this.panelWidth / 2 &&
      Math.abs(pointer.y - center.y) <= this.panelHeight / 2
    );
  }

  /** 스크롤 가능한 최대 줄. 0이면 스크롤할 내용이 없다. */
  private getMaxScrollRow(): number {
    return Math.max(0, this.entries.length - VISIBLE_ROWS);
  }

  private setScrollRow(row: number): void {
    const clamped = Phaser.Math.Clamp(row, 0, this.getMaxScrollRow());
    if (clamped === this.scrollRow) return;
    this.scrollRow = clamped;
    this.renderRows();
  }

  /** 트랙에서의 포인터 Y를 스크롤 줄로 환산한다. */
  private scrubToPointer(pointer: Phaser.Input.Pointer): void {
    const maxScroll = this.getMaxScrollRow();
    if (maxScroll === 0) return;

    const localY = pointer.y - this.getScreenCenter().y;
    const ratio = Phaser.Math.Clamp((localY - this.listTop) / this.listHeight, 0, 1);
    this.setScrollRow(Math.round(ratio * maxScroll));
  }

  /** 스크롤 위치·내용량에 맞춰 썸의 크기와 위치를 다시 계산한다. */
  private updateScrollBar(): void {
    const maxScroll = this.getMaxScrollRow();
    const needed = maxScroll > 0;
    this.scrollTrack.setVisible(needed);
    this.scrollThumb.setVisible(needed);
    if (!needed) return;

    const thumbHeight = Math.max(20, (this.listHeight * VISIBLE_ROWS) / this.entries.length);
    const travel = this.listHeight - thumbHeight;

    this.scrollThumb.setSize(this.scrollThumb.width, thumbHeight);
    this.scrollThumb.y = this.listTop + thumbHeight / 2 + (this.scrollRow / maxScroll) * travel;
  }

  // ==================== 목록 구독 ====================

  /**
   * 상점 목록을 스스로 구독한다.
   *
   * `shop:close`는 GameState가 목록을 비울 때(맵 이동·로그아웃 등) 온다 —
   * 창만 남고 내용이 사라지는 상태를 막으려면 함께 닫아야 한다.
   */
  private subscribeShopEvents(): void {
    this.unsubscribers.push(
      gameState.onShopSellList((data) => this.showSellList(data)),
      gameState.onShopBuyList((data) => this.showBuyList(data)),
      gameState.onShopClose(() => this.hide(false))
    );

    this.once(Phaser.GameObjects.Events.DESTROY, () => {
      this.unsubscribers.forEach((off) => off());
      this.unsubscribers = [];
    });
  }

  /** S_ShopSellList — NPC가 파는 물건 = 플레이어의 **구매** 목록. */
  private showSellList(data: ShopSellListPayload): void {
    const entries: ShopRowData[] = (data.items ?? []).map((item) => ({
      id: item.index,
      // 묶음 판매는 서버가 이름 꼬리에 묶음 수량을 박아 보낸다 — 값에 포함된
      // 정보이므로 떼지 않는다 (types/network.ts의 ShopSellListItem 주석 참조).
      name: item.name,
      price: item.price,
      gfxId: item.gfxId,
    }));
    this.open('buy', data.objId, data.currency ?? 0x07, entries);
  }

  /** S_ShopBuyList — NPC가 사주는 값 = 플레이어의 **판매** 목록. */
  private showBuyList(data: ShopBuyListState): void {
    const entries: ShopRowData[] = (data.items ?? []).map((entry) => ({
      id: entry.objId,
      // 서버는 objId와 가격만 보낸다. GameState가 인벤토리에서 이름을 찾아
      // 붙여 주지만 못 찾을 수도 있어(방금 사라진 아이템 등) 빈 이름을 허용한다.
      name: entry.name ?? '',
      price: entry.price,
      gfxId: entry.gfxId,
      count: entry.count,
    }));
    this.open('sell', data.objId, data.currency ?? 0x07, entries);
  }

  /** 목록을 채우고 창을 연다. */
  private open(mode: ShopMode, npcObjId: number, currency: number, entries: ShopRowData[]): void {
    this.mode = mode;
    this.npcObjId = npcObjId;
    this.currency = currency;
    this.entries = entries;

    this.scrollRow = 0;
    this.selectedId = null;
    this.quantity = 1;
    this.statusText.setText('');

    const npcName = gameState.getNpc(npcObjId)?.name;
    const modeLabel = mode === 'buy' ? '구매' : '판매';
    this.titleText.setText(npcName ? `상점 - ${npcName} [${modeLabel}]` : `상점 [${modeLabel}]`);
    this.confirmText.setText(`[ ${modeLabel} ]`);

    this.emptyText.setText(
      mode === 'buy' ? '파는 물건이 없습니다' : '팔 수 있는 물건이 없습니다'
    );

    this.renderRows();
    this.refreshFooter();

    this.setVisible(true);
    this.onVisibilityChange?.();
  }

  /**
   * 창을 닫는다.
   *
   * @param notify false면 `onDismiss`를 부르지 않는다 — 부모가 창 전체를 접는
   *   경로(ESC 등)에서 대화창이 되살아나지 않게 하기 위한 것.
   */
  public hide(notify: boolean = true): void {
    if (!this.visible) return;
    this.setVisible(false);
    this.selectedId = null;

    // 목록의 수명은 창의 수명과 같다 — 닫으면 GameState의 캐시도 비운다
    // (GameState.clearShopLists 주석: "상점 창을 닫거나 맵이 바뀌면 비운다").
    // 이때 발행되는 shop:close가 다시 여기로 돌아오지만 이미 숨겨져 있어 멈춘다.
    gameState.clearShopLists();

    this.onVisibilityChange?.();
    if (notify) this.onDismiss?.();
  }

  // ==================== 목록 렌더 ====================

  /** 현재 스크롤 위치에 해당하는 구간만 줄에 그린다. */
  private renderRows(): void {
    const showCount = this.mode === 'sell';

    this.rows.forEach((row, i) => {
      const entry = this.entries[this.scrollRow + i];
      if (entry) {
        row.setEntry(entry, this.currency, showCount, entry.id === this.selectedId);
      } else {
        row.clear();
      }
    });

    this.emptyText.setVisible(this.entries.length === 0);
    this.updateScrollBar();
    this.updateCountText();
  }

  private updateCountText(): void {
    const total = this.entries.length;
    if (total === 0) {
      this.countText.setText('0 / 0');
      return;
    }
    const first = this.scrollRow + 1;
    const last = Math.min(total, this.scrollRow + VISIBLE_ROWS);
    this.countText.setText(`${first}-${last} / ${total}`);
  }

  // ==================== 선택 · 수량 · 거래 ====================

  private getSelected(): ShopRowData | null {
    if (this.selectedId === null) return null;
    return this.entries.find((entry) => entry.id === this.selectedId) ?? null;
  }

  /** 선택한 항목이 한 번에 거래할 수 있는 최대 수량. */
  private getMaxQuantity(entry: ShopRowData): number {
    // 판매는 소지 개수를 넘길 수 없다. 개수를 모르면(인벤토리에서 못 찾은 항목)
    // 1개만 허용한다 — 서버가 실제 개수로 잘라 주지만 화면이 거짓말을 하면 안 된다.
    if (this.mode === 'sell') return Math.max(1, entry.count ?? 1);
    return MAX_BUY_COUNT;
  }

  private select(entry: ShopRowData): void {
    this.selectedId = entry.id;
    this.quantity = 1;
    this.statusText.setText('');
    this.renderRows();
    this.refreshFooter();
  }

  private changeQuantity(delta: number): void {
    const entry = this.getSelected();
    if (!entry) return;
    this.quantity = Phaser.Math.Clamp(this.quantity + delta, 1, this.getMaxQuantity(entry));
    this.refreshFooter();
  }

  /** 선택 정보 · 수량 · 총액 표시를 다시 그린다. */
  private refreshFooter(): void {
    const entry = this.getSelected();
    const unit = currencyName(this.currency);

    if (!entry) {
      this.infoText.setText('항목을 선택하세요');
      this.infoText.setStyle({ color: '#888888' });
      this.qtyText.setText('-');
      this.totalText.setText('');
      return;
    }

    const label = entry.name || `#${entry.id}`;
    const owned = entry.count !== undefined ? ` · 소지 ${entry.count}` : '';
    this.infoText.setText(`선택: ${label} · 개당 ${formatPrice(entry.price)} ${unit}${owned}`);
    this.infoText.setStyle({ color: '#ffffff' });
    this.qtyText.setText(String(this.quantity));
    this.totalText.setText(`총액 ${formatPrice(entry.price * this.quantity)} ${unit}`);
  }

  /**
   * 거래를 확정한다 (C_Result).
   *
   * 서버는 NPC와의 거리가 3타일을 넘으면 조용히 무시하고, 아데나가 모자라면
   * `S_ServerMessage`로 거절한다 — 성공/실패 응답이 따로 오지 않으므로 여기서는
   * 보내는 것까지만 책임지고 창을 닫는다. 결과는 인벤토리 갱신으로 드러난다.
   */
  private confirmTrade(): void {
    const entry = this.getSelected();
    if (!entry) {
      this.statusText.setText('먼저 항목을 선택하세요.');
      return;
    }

    const connection = ConnectionManager.getInstance();
    if (!connection.isConnected()) {
      this.statusText.setText('서버와 연결되어 있지 않습니다.');
      return;
    }

    // resultType 0 = 구매(플레이어가 산다), 1 = 판매(플레이어가 판다).
    // id의 정체가 모드마다 다르다 — 파일 상단 주석 참조.
    const resultType = this.mode === 'buy' ? 0 : 1;
    const count = Phaser.Math.Clamp(this.quantity, 1, this.getMaxQuantity(entry));

    connection.getPacketHandler().sendShopResult(this.npcObjId, resultType, [
      { id: entry.id, count },
    ]);

    this.hide();
    // 거래 클릭도 창을 닫는 클릭이므로 월드로 새지 않게 알린다 (닫기 버튼과 같은 이유).
    this.emit('panelclose');
  }

  /** Container.setVisible()은 void를 반환한다 — 체이닝을 살려 둔다. */
  public setVisible(visible: boolean): this {
    super.setVisible(visible);
    return this;
  }
}

/**
 * 상점 목록의 한 줄.
 *
 * 아이콘 + 이름(+ 소지 개수) + 가격을 보여주고 클릭하면 선택을 알린다.
 * 스크롤로 내용이 갈리므로 줄 자체는 선택 상태를 갖지 않는다 — 어느 항목이
 * 선택됐는지는 창이 알고, 줄은 그려 달라는 대로 테두리만 바꾼다.
 */
class ShopRow extends Phaser.GameObjects.Container {
  private bg!: Phaser.GameObjects.Rectangle;
  private iconImage!: Phaser.GameObjects.Image;
  private iconText!: Phaser.GameObjects.Text;
  private nameText!: Phaser.GameObjects.Text;
  private countText!: Phaser.GameObjects.Text;
  private priceText!: Phaser.GameObjects.Text;

  private entry: ShopRowData | null = null;
  private selected = false;

  /**
   * 아이콘 로드가 비동기라, 로드 중에 스크롤로 내용이 바뀌면 늦게 도착한 텍스처가
   * 엉뚱한 그림을 붙인다. 내용을 갈아 끼울 때마다 증가시켜 뒤늦은 응답을 버린다
   * (DragGhost와 같은 방식).
   */
  private token = 0;

  private onPick: (entry: ShopRowData) => void;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    onPick: (entry: ShopRowData) => void
  ) {
    super(scene, x, y);
    this.onPick = onPick;

    this.createUI(width, height);
    scene.add.existing(this);
  }

  private createUI(width: number, height: number): void {
    const iconSize = height - 8;

    this.bg = this.scene.add.rectangle(0, 0, width, height, ROW_BG_COLOR);
    this.bg.setStrokeStyle(1, ROW_BORDER_COLOR);
    this.add(this.bg);

    this.iconImage = this.scene.add.image(-width / 2 + 6 + iconSize / 2, 0, '__DEFAULT');
    this.iconImage.setVisible(false);
    this.add(this.iconImage);

    // 아이콘 텍스처가 아직 없을 때의 폴백. 칸이 비어 보이지 않게 한다.
    this.iconText = this.scene.add.text(-width / 2 + 6 + iconSize / 2, 0, '', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#aaaaaa',
    });
    this.iconText.setOrigin(0.5);
    this.add(this.iconText);

    // 이름이 길어도 오른쪽의 개수·가격을 밀어내지 않도록 한 줄로 자른다.
    const textLeft = -width / 2 + 12 + iconSize;
    this.nameText = this.scene.add.text(textLeft, 0, '', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      wordWrap: { width: width - iconSize - 12 - 150 },
      maxLines: 1,
    });
    this.nameText.setOrigin(0, 0.5);
    this.add(this.nameText);

    // 소지 개수는 판매 모드에서만 쓰는 보조 정보라 가격 왼쪽에 흐리게 붙인다.
    this.countText = this.scene.add.text(width / 2 - 130, 0, '', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#8899bb',
    });
    this.countText.setOrigin(1, 0.5);
    this.add(this.countText);

    this.priceText = this.scene.add.text(width / 2 - 8, 0, '', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
    });
    this.priceText.setOrigin(1, 0.5);
    this.add(this.priceText);

    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다.
    const hitArea = this.scene.add.rectangle(0, 0, width, height, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    hitArea.setScrollFactor(0);
    hitArea.on('pointerover', () => {
      if (!this.entry) return;
      this.bg.setFillStyle(ROW_BG_HOVER_COLOR);
      this.bg.setStrokeStyle(1, ROW_BORDER_HOVER_COLOR);
    });
    hitArea.on('pointerout', () => this.resetStyle());
    hitArea.on('pointerdown', () => {
      if (!this.entry) return;
      this.onPick(this.entry);
    });
    this.add(hitArea);
  }

  public setEntry(entry: ShopRowData, currency: number, showCount: boolean, selected: boolean): void {
    const token = ++this.token;
    this.entry = entry;
    this.selected = selected;

    // 이름이 없는 항목(판매 목록에서 인벤토리 조인 실패)도 objId로 식별되게 한다.
    this.nameText.setText(entry.name || `#${entry.id}`);
    this.nameText.setStyle({ color: entry.name ? '#ffffff' : '#bbbbcc' });
    this.countText.setText(showCount && entry.count !== undefined ? `x${entry.count}` : '');
    this.priceText.setText(`${formatPrice(entry.price)} ${currencyName(currency)}`);

    this.bg.setVisible(true);
    this.applyIcon(entry, token);
    this.resetStyle();
  }

  /** 빈 줄로 되돌린다. 배경까지 숨겨야 목록이 짧을 때 빈 칸이 눈에 남지 않는다. */
  public clear(): void {
    this.token++;
    this.entry = null;
    this.selected = false;
    this.nameText.setText('');
    this.countText.setText('');
    this.priceText.setText('');
    this.iconImage.setVisible(false);
    this.iconText.setText('');
    this.bg.setVisible(false);
    this.resetStyle();
  }

  private applyIcon(entry: ShopRowData, token: number): void {
    const kind = itemIconKind(entry.gfxId ?? 0, entry.name);
    const key = itemIconTextureKey(kind);

    if (this.scene.textures.exists(key)) {
      this.showTexture(key);
      return;
    }

    this.iconImage.setVisible(false);
    this.iconText.setText('*');
    loadItemIcon(this.scene, kind)
      .then((loadedKey) => {
        if (token !== this.token) return;
        this.showTexture(loadedKey);
      })
      .catch(() => {
        /* 폴백 문자가 이미 떠 있다 */
      });
  }

  private showTexture(key: string): void {
    this.iconImage.setTexture(key);
    // setTexture()는 새 프레임 크기로 width/height를 되돌리므로 매번 다시 잡는다.
    this.iconImage.setDisplaySize(ROW_HEIGHT - 12, ROW_HEIGHT - 12);
    this.iconImage.setVisible(true);
    this.iconText.setText('');
  }

  private resetStyle(): void {
    this.bg.setFillStyle(ROW_BG_COLOR);
    this.bg.setStrokeStyle(1, this.selected ? ROW_BORDER_SELECTED_COLOR : ROW_BORDER_COLOR);
  }
}
